/**
 * TicketDetailModal - Trello-style modal overlay showing full ticket details.
 * @implements FS-005.9.3 - Ticket Detail Modal
 * @implements FS-005.9.3.1 - Modal Trigger (opens on card click, closes on backdrop/escape)
 * @implements FS-005.9.3.2 - Modal Content (all ticket content sections)
 *
 * Features:
 * - Semi-transparent backdrop, click to close
 * - Header with title, type badge, column indicator, close button
 * - Metadata section with lifecycle state and validation status
 * - Description section with markdown rendering
 * - Acceptance criteria checklist (read-only)
 * - Children section with clickable child tickets
 * - Validation feedback section (for consolidation tickets)
 * - Context section (if present)
 * - Spec references section
 * - Responsive: full width on mobile
 */

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Article as SpecIcon,
  Person as AgentIcon,
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import { KanbanTicketPayload } from '@/stores/KanbanStore';
import { KanbanColumnBadge, KanbanColumn } from './KanbanColumnBadge';
import {
  LifecycleIndicator,
  RoadmapLifecycleState,
} from './LifecycleIndicator';
import {
  ValidationStatusBadge,
  ValidationStatus,
} from './ValidationStatusBadge';

/**
 * Type-to-color mapping for ticket type badges.
 * Matches KanbanCard.tsx TYPE_BADGE_COLORS.
 * @implements FS-005.9.2.3 - Type-Based Visual Differentiation
 */
const TYPE_BADGE_COLORS: Record<string, string> = {
  MILESTONE: '#9c27b0', // Purple
  EPIC: '#1976d2', // Blue
  USER_STORY: '#2e7d32', // Green
  TECHNICAL_STORY: '#ed6c02', // Orange
};

/**
 * Human-readable display names for ticket types.
 */
const TYPE_DISPLAY_NAMES: Record<string, string> = {
  MILESTONE: 'Milestone',
  EPIC: 'Epic',
  USER_STORY: 'User Story',
  TECHNICAL_STORY: 'Technical Story',
};

/**
 * Human-readable display names for agent types.
 */
const AGENT_DISPLAY_NAMES: Record<string, string> = {
  BACKEND_DEV: 'Backend Developer',
  FRONTEND_DEV: 'Frontend Developer',
  QA: 'QA Engineer',
};

/**
 * Props for TicketDetailModal component.
 */
export interface TicketDetailModalProps {
  /** The ticket to display, or null if modal is closed */
  ticket: KanbanTicketPayload | null;
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Callback when a child ticket is clicked */
  onChildClick?: (childId: string) => void;
  /** Validation status for the ticket (from server state) */
  validationStatus?: ValidationStatus | null;
}

/**
 * TicketDetailModal - Modal showing full ticket details.
 *
 * @implements FS-005.9.3.1 - Modal Trigger
 * @implements FS-005.9.3.2 - Modal Content
 *
 * Sections:
 * 1. Header: title, type badge, column badge, close button
 * 2. Metadata: lifecycle state, validation status
 * 3. Description: markdown-rendered content
 * 4. Acceptance Criteria: read-only checklist with progress
 * 5. Children: clickable list of child tickets
 * 6. Validation Feedback: for consolidation tickets
 * 7. Context: additional context if present
 * 8. Spec References: list of FS-XXX references
 */
export const TicketDetailModal = ({
  ticket,
  open,
  onClose,
  onChildClick,
  validationStatus,
}: TicketDetailModalProps) => {
  if (!ticket) return null;

  const typeColor = TYPE_BADGE_COLORS[ticket.type] || '#757575';
  const typeLabel = TYPE_DISPLAY_NAMES[ticket.type] || ticket.type;

  // Acceptance criteria progress (all items are read-only, showing total)
  const totalAC = ticket.acceptanceCriteria?.length || 0;
  // Note: Current payload has AC as strings; completion tracking would need
  // structured data. For now, show all as unchecked.
  const completedAC = 0;

  // Check if ticket is in consolidation for validation feedback display
  const isInConsolidation = ticket.column === 'consolidation';

  // Filter children that have IDs (non-null) for clickable list
  const clickableChildren = ticket.children?.filter((c) => c.id !== null) || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="ticket-detail-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          // Full screen on mobile
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
          height: { xs: '100%', sm: 'auto' },
        },
      }}
      data-testid="ticket-detail-modal"
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
          {/* Type Badge */}
          <Chip
            label={typeLabel}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: typeColor,
              color: '#fff',
              mb: 1,
              '& .MuiChip-label': { px: 1 },
            }}
          />

          {/* Title */}
          <Typography
            id="ticket-detail-title"
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {ticket.title}
          </Typography>

          {/* Column Badge */}
          <KanbanColumnBadge column={ticket.column as KanbanColumn} />
        </Box>

        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
          }}
          data-testid="ticket-detail-close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Metadata Section */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <LifecycleIndicator
            state={ticket.lifecycleState as RoadmapLifecycleState}
          />
          {isInConsolidation && (
            <ValidationStatusBadge status={validationStatus} />
          )}
        </Box>

        <Divider />

        {/* Description Section */}
        {ticket.description && (
          <>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                }}
              >
                Description
              </Typography>
              <Box
                sx={{
                  '& p': { mt: 0, mb: 1.5 },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    mt: 2,
                    mb: 1,
                    fontWeight: 600,
                  },
                  '& ul, & ol': { pl: 3, my: 1 },
                  '& li': { mb: 0.5 },
                  '& code': {
                    bgcolor: 'grey.100',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.875em',
                    fontFamily: 'monospace',
                  },
                  '& pre': {
                    bgcolor: 'grey.100',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    '& code': {
                      bgcolor: 'transparent',
                      p: 0,
                    },
                  },
                  '& blockquote': {
                    borderLeft: 4,
                    borderColor: 'grey.300',
                    pl: 2,
                    ml: 0,
                    color: 'text.secondary',
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                }}
              >
                <Markdown>{ticket.description}</Markdown>
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {/* Acceptance Criteria Section */}
        {totalAC > 0 && (
          <>
            <Box sx={{ px: 3, py: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  Acceptance Criteria
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                >
                  {completedAC} of {totalAC} complete
                </Typography>
              </Box>

              {/* Progress Bar */}
              <LinearProgress
                variant="determinate"
                value={totalAC > 0 ? (completedAC / totalAC) * 100 : 0}
                sx={{ mb: 2, borderRadius: 1, height: 6 }}
              />

              {/* Checklist (read-only) */}
              <List disablePadding dense>
                {ticket.acceptanceCriteria.map((criterion, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                    <Checkbox
                      checked={false}
                      disabled
                      size="small"
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <ListItemText
                      primary={criterion}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.primary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Divider />
          </>
        )}

        {/* Children Section */}
        {clickableChildren.length > 0 && (
          <>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                }}
              >
                Child Tickets ({clickableChildren.length})
              </Typography>

              <List disablePadding>
                {clickableChildren.map((child) => (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => child.id && onChildClick?.(child.id)}
                      sx={{
                        borderRadius: 1,
                        py: 1,
                        px: 1.5,
                        bgcolor: 'grey.50',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      data-testid={`child-ticket-${child.id}`}
                    >
                      <ListItemText
                        primary={child.text}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Divider />
          </>
        )}

        {/* Validation Feedback Section (only for consolidation) */}
        {isInConsolidation &&
          ticket.validationFeedback &&
          ticket.validationFeedback.length > 0 && (
            <>
              <Box sx={{ px: 3, py: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 1.5,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  Validation Feedback
                </Typography>

                <List disablePadding>
                  {ticket.validationFeedback.map((fb, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        mb: 1,
                        p: 1.5,
                      }}
                    >
                      {/* Agent and Status */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                          width: '100%',
                        }}
                      >
                        <AgentIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, flex: 1 }}
                        >
                          {AGENT_DISPLAY_NAMES[fb.agentType] || fb.agentType}
                        </Typography>
                        <Chip
                          label={
                            fb.status === 'APPROVED' ? 'Approved' : 'Issues'
                          }
                          size="small"
                          color={fb.status === 'APPROVED' ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>

                      {/* Feedback Text */}
                      {fb.feedback && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {fb.feedback}
                        </Typography>
                      )}

                      {/* Timestamp */}
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', mt: 0.5 }}
                      >
                        {new Date(fb.timestamp).toLocaleString()}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Divider />
            </>
          )}

        {/* Context Section */}
        {ticket.context && (
          <>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                }}
              >
                Context
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {ticket.context}
              </Typography>
            </Box>
            <Divider />
          </>
        )}

        {/* Spec References Section */}
        {ticket.specReferences && ticket.specReferences.length > 0 && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 1,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              Spec References
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {ticket.specReferences.map((ref, index) => (
                <Chip
                  key={index}
                  icon={<SpecIcon sx={{ fontSize: 16 }} />}
                  label={ref}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 26,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    '& .MuiChip-icon': { ml: 0.5 },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Display name for React DevTools
TicketDetailModal.displayName = 'TicketDetailModal';
